import { Avatar, Button, Dropdown, Layout, Menu, Drawer, Grid, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, ChevronsDown, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAuthStore } from '@features/auth/store';
import { type Permission } from '@shared/rbac/permissions.const';
import { hasPermission } from '@shared/rbac/permissionMap';
import { useRoleLabel } from '@shared/constants/labels';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@shared/stores/uiStore';
import {
  NAV,
  filterNav,
  findItemByKey,
  findOpenKeys,
  findSelectedKeys,
  type NavItem,
} from './sidebarNav';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

type AntMenuItems = NonNullable<MenuProps['items']>;

function toMenuItems(
  items: NavItem[],
  collapsed: boolean,
  t: (key: string) => string,
): AntMenuItems {
  return items.map((it) => {
    const label = t(it.labelKey);
    return it.children?.length
      ? {
          key: it.key,
          icon: it.icon,
          label,
          children: toMenuItems(it.children, collapsed, t),
          title: collapsed ? label : undefined,
        }
      : {
          key: it.key,
          icon: it.icon,
          label,
          title: collapsed ? label : undefined,
        };
  });
}

interface Workspace {
  id: string;
  nameKey: string;
  subKey: string;
  mark: string;
}

const DEFAULT_WORKSPACE: Workspace = {
  id: 'emlak-katilim',
  nameKey: 'workspace.emlakKatilim',
  subKey: 'workspace.emlakKatilimSub',
  mark: 'EK',
};

const WORKSPACES: Workspace[] = [DEFAULT_WORKSPACE];

function SidebarBrand({ collapsed }: { collapsed: boolean }) {
  // Menu labels are data-driven (keys live in NAV/WORKSPACES), so use a
  // string-keyed `t` instead of the literal-key-typed one.
  const { t: tNav } = useTranslation('nav');
  const t = tNav as unknown as (key: string) => string;
  const workspaceId = useUiStore((s) => s.workspaceId);
  const setWorkspace = useUiStore((s) => s.setWorkspace);
  const active: Workspace = WORKSPACES.find((w) => w.id === workspaceId) ?? DEFAULT_WORKSPACE;

  const menu = {
    items: [
      {
        key: 'header',
        type: 'group' as const,
        label: (
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('workspace.groupLabel')}
          </div>
        ),
      },
      ...WORKSPACES.map((w) => ({
        key: w.id,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: w.id === active.id ? 'var(--ds-brand)' : 'var(--ds-bg-muted)',
                color: w.id === active.id ? 'var(--ds-text-on-brand)' : 'var(--ds-text-secondary)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {w.mark}
            </span>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{t(w.nameKey)}</div>
              <div style={{ fontSize: 11, color: 'var(--ds-text-tertiary)' }}>{t(w.subKey)}</div>
            </div>
            {w.id === active.id ? (
              <span style={{ marginInlineStart: 'auto', color: 'var(--ds-brand)', fontSize: 12 }}>✓</span>
            ) : null}
          </div>
        ),
        disabled: w.id !== 'emlak-katilim',
      })),
    ],
    onClick: ({ key }: { key: string }) => {
      if (key !== 'header') setWorkspace(key);
    },
  };

  // With a single workspace there's nothing to switch to — render a static
  // brand block (no dropdown affordance) instead of a clickable button.
  if (WORKSPACES.length <= 1) {
    return (
      <div className="ds-sidebar__brand">
        <div className="ds-sidebar__brand-mark" aria-hidden="true">
          <img src="/logo.svg" alt="" />
        </div>
        {!collapsed ? (
          <div className="ds-sidebar__brand-text">
            <span className="ds-sidebar__brand-name">{t(active.nameKey)}</span>
            <span className="ds-sidebar__brand-sub">{t(active.subKey)}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Dropdown menu={menu} placement="bottomLeft" trigger={['click']}>
      <button
        type="button"
        className="ds-sidebar__brand ds-sidebar__brand--button"
        aria-label={t('workspace.pickAria')}
      >
        <div className="ds-sidebar__brand-mark" aria-hidden="true">
          <img src="/logo.svg" alt="" />
        </div>
        {!collapsed ? (
          <>
            <div className="ds-sidebar__brand-text">
              <span className="ds-sidebar__brand-name">{t(active.nameKey)}</span>
              <span className="ds-sidebar__brand-sub">{t(active.subKey)}</span>
            </div>
            <ChevronsDown size={14} style={{ color: 'var(--ds-text-tertiary)' }} />
          </>
        ) : null}
      </button>
    </Dropdown>
  );
}

function SidebarMenu({
  collapsed,
  selectedKeys,
  openKeys,
  onOpenChange,
  onSelect,
}: {
  collapsed: boolean;
  selectedKeys: string[];
  openKeys: string[];
  onOpenChange: (keys: string[]) => void;
  onSelect: (key: string) => void;
}) {
  const { t: tNav } = useTranslation('nav');
  const t = tNav as unknown as (key: string) => string;
  const { user } = useAuth();
  const can = (permission: Permission) => hasPermission(permission, user?.permissions, user?.roles);
  const sections = filterNav(NAV, can);
  return (
    <div className="ds-sidebar__menus">
      {sections.map((section, idx) => (
        <div key={section.key}>
          {section.titleKey && !collapsed ? (
            <div className="ds-sidebar__section-title">{t(section.titleKey)}</div>
          ) : null}
          {section.titleKey && collapsed && idx > 0 ? (
            <div className="ds-sidebar__divider" aria-hidden="true" />
          ) : null}
          <Menu
            mode="inline"
            theme="light"
            inlineIndent={16}
            inlineCollapsed={collapsed}
            items={toMenuItems(section.items, collapsed, t)}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={(keys) => onOpenChange(keys)}
            onClick={({ key }) => onSelect(key)}
            style={{ borderInlineEnd: 0, background: 'transparent' }}
          />
        </div>
      ))}
    </div>
  );
}

function SidebarMobileUserFooter({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation('nav');
  const roleLabel = useRoleLabel();
  const { user } = useAuth();
  const initials = user?.fullName
    ?.split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (!user) return null;
  return (
    <div className="ds-sidebar-mobile-user">
      <div className="ds-sidebar-mobile-user__row">
        <span className="ds-avatar-wrap">
          <Avatar
            size={36}
            src={resolveAssetUrl(user?.avatarUrl) ?? undefined}
            style={{ background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)', fontWeight: 600 }}
          >
            {initials ?? 'HD'}
          </Avatar>
          <span className="ds-avatar-wrap__status" aria-label={t('sidebar.online')} />
        </span>
        <div className="ds-sidebar-mobile-user__meta">
          <div className="ds-sidebar-mobile-user__name">{user.fullName}</div>
          <div className="ds-sidebar-mobile-user__role">
            {roleLabel(user.role)} · {user.email}
          </div>
        </div>
      </div>
      <Button block icon={<LogOut size={14} />} onClick={onLogout} danger>
        {t('sidebar.logout')}
      </Button>
    </div>
  );
}

export function Sidebar() {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const location = useLocation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const mobileOpen = useUiStore((s) => s.mobileDrawerOpen);
  const setMobileOpen = useUiStore((s) => s.setMobileDrawerOpen);
  const clearSession = useAuthStore((s) => s.clearSession);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const selectedKeys = findSelectedKeys(location.pathname);
  const routeOpenKeys = findOpenKeys(location.pathname);

  // Controlled openKeys: start with the route's parent expanded, allow user
  // to manually open more / close the current one. On every navigation, union
  // the current state with the new route's parent so the active section is
  // always visible (avoids the "submenu stays collapsed after navigation" bug).
  // Effect'siz "adjust state on navigation" deseni: route değişimini render
  // sırasında yakala (guard'lı setState — set-state-in-effect'ten kaçınır).
  const [openKeys, setOpenKeys] = useState<string[]>(routeOpenKeys);
  const routeKeySignature = routeOpenKeys.join('|');
  const [prevRouteKeySignature, setPrevRouteKeySignature] = useState(routeKeySignature);
  if (routeKeySignature !== prevRouteKeySignature) {
    setPrevRouteKeySignature(routeKeySignature);
    if (!routeOpenKeys.every((k) => openKeys.includes(k))) {
      setOpenKeys(Array.from(new Set([...openKeys, ...routeOpenKeys])));
    }
  }

  const handleSelect = (key: string) => {
    const item = findItemByKey(key);
    if (item) {
      void navigate(item.to);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleMobileLogout = () => {
    clearSession();
    setMobileOpen(false);
    void navigate('/login', { replace: true });
  };

  if (isMobile) {
    return (
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        placement="left"
        width={300}
        closable={false}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
        rootClassName="ds-sidebar-drawer"
      >
        <SidebarBrand collapsed={false} />
        <SidebarMenu
          collapsed={false}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onSelect={handleSelect}
        />
        <SidebarMobileUserFooter onLogout={handleMobileLogout} />
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={toggle}
      trigger={null}
      width={248}
      collapsedWidth={64}
      theme="light"
      className="ds-sidebar"
      style={{ height: '100vh', position: 'sticky', top: 0, overflow: 'auto' }}
    >
      <div className="ds-sidebar__inner">
        <SidebarBrand collapsed={collapsed} />
        <SidebarMenu
          collapsed={collapsed}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onSelect={handleSelect}
        />
        <Tooltip
          title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          placement="right"
        >
          <button
            type="button"
            onClick={toggle}
            className="ds-sidebar__collapse-btn"
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            data-collapsed={collapsed ? 'true' : 'false'}
          >
            {collapsed ? (
              <ChevronsRight size={16} strokeWidth={1.8} />
            ) : (
              <ChevronsLeft size={16} strokeWidth={1.8} />
            )}
            {!collapsed ? <span>{t('sidebar.collapseShort')}</span> : null}
          </button>
        </Tooltip>
      </div>
    </Sider>
  );
}
